import { useLiveQuery } from 'dexie-react-hooks'
import { AnimatePresence, motion } from 'framer-motion'
import { Check, Drumstick, PackageSearch, TriangleAlert, Warehouse } from 'lucide-react'
import { Fragment, useEffect, useMemo, useRef, useState } from 'react'

import { ScreenHeader } from '@/components/ScreenHeader'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import type { Produs } from '@/db/dexie'
import { getProduse, setPretProdus } from '@/db/queries'
import { fmtKg, fmtRON, fmtSuma } from '@/lib/format'
import { cn } from '@/lib/utils'

const PRAG_STOC = 80

/** Category order + warm-editorial badge variant. */
const CATEGORII = ['Pui', 'Organe', 'Procesate'] as const
type Categorie = (typeof CATEGORII)[number]

const catVariant: Record<string, 'warning' | 'danger' | 'success' | 'muted'> = {
  Pui: 'warning',
  Organe: 'danger',
  Procesate: 'success',
}

/** RO-style editable draft: "26,50" — comma decimal, no thousands separator (round-trips cleanly). */
const toDraft = (n: number) => n.toFixed(2).replace('.', ',')

/**
 * Inline, live-saved price editor. Keeps a local draft while focused so the
 * cursor never jumps, commits to Dexie on blur/Enter, and flashes a "salvat"
 * check. Reverts to the stored value on an invalid entry. Displays RO-style
 * with a comma; accepts either comma or dot on input.
 */
function PretInput({ id, value }: { id: string; value: number }) {
  const [text, setText] = useState(() => toDraft(value))
  const [saved, setSaved] = useState(false)
  const focused = useRef(false)

  useEffect(() => {
    if (!focused.current) setText(toDraft(value))
  }, [value])

  const commit = async () => {
    focused.current = false
    const n = Number.parseFloat(text.replace(',', '.'))
    if (Number.isFinite(n) && n > 0 && Math.abs(n - value) > 0.001) {
      await setPretProdus(id, n)
      setSaved(true)
      window.setTimeout(() => setSaved(false), 1300)
    } else {
      setText(toDraft(value))
    }
  }

  return (
    <div className="flex items-center justify-end gap-1.5">
      <div className="relative">
        <Input
          value={text}
          inputMode="decimal"
          aria-label="Preț pe kilogram"
          onFocus={(e) => {
            focused.current = true
            e.currentTarget.select()
          }}
          onChange={(e) => setText(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.currentTarget.blur()
            if (e.key === 'Escape') {
              setText(toDraft(value))
              e.currentTarget.blur()
            }
          }}
          className="h-8 w-24 pr-9 text-right font-mono text-sm nums"
        />
        <span className="pointer-events-none absolute inset-y-0 right-2.5 flex items-center text-[0.7rem] font-medium text-muted-foreground">
          lei
        </span>
      </div>
      <AnimatePresence>
        {saved && (
          <motion.span
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.6 }}
            transition={{ duration: 0.2 }}
            className="flex size-5 items-center justify-center rounded-full bg-pine-soft text-pine"
            title="Salvat"
          >
            <Check className="size-3" />
          </motion.span>
        )}
      </AnimatePresence>
    </div>
  )
}

function StatTile({
  label,
  value,
  icon: Icon,
  tone = 'ink',
  index = 0,
}: {
  label: string
  value: string
  icon: typeof Warehouse
  tone?: 'ink' | 'amber' | 'tomato'
  index?: number
}) {
  const toneClass =
    tone === 'amber' ? 'text-amber-ink' : tone === 'tomato' ? 'text-tomato' : 'text-foreground'
  const iconWrap = tone === 'tomato' ? 'bg-tomato-soft text-tomato' : 'bg-amber-soft text-amber-ink'
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, delay: index * 0.05, ease: [0.16, 1, 0.3, 1] }}
      className="flex items-center gap-3 rounded-xl border border-border bg-card px-4 py-3 shadow-paper"
    >
      <span className={cn('flex size-9 shrink-0 items-center justify-center rounded-lg', iconWrap)}>
        <Icon className="size-4" />
      </span>
      <div className="min-w-0">
        <div className="text-[0.7rem] font-medium uppercase tracking-wide text-muted-foreground">
          {label}
        </div>
        <div className={cn('font-mono text-lg font-semibold tracking-tight nums', toneClass)}>
          {value}
        </div>
      </div>
    </motion.div>
  )
}

export default function Catalog() {
  const produse = useLiveQuery(() => getProduse(), [])
  const [cauta, setCauta] = useState('')
  const [filtruCat, setFiltruCat] = useState<'toate' | Categorie>('toate')

  const filtrate = useMemo(() => {
    if (!produse) return []
    const q = cauta.trim().toLowerCase()
    return produse.filter((p) => {
      if (filtruCat !== 'toate' && p.categorie !== filtruCat) return false
      if (!q) return true
      return p.nume.toLowerCase().includes(q) || p.cod.toLowerCase().includes(q)
    })
  }, [produse, cauta, filtruCat])

  const summary = useMemo(() => {
    const list = produse ?? []
    return {
      nr: list.length,
      valoare: list.reduce((s, p) => s + p.stocKg * p.pretKg, 0),
      totalKg: list.reduce((s, p) => s + p.stocKg, 0),
      subPrag: list.filter((p) => p.stocKg < PRAG_STOC).length,
    }
  }, [produse])

  // group filtered rows by category, preserving CATEGORII order
  const grupuri = useMemo(() => {
    const by = new Map<string, Produs[]>()
    for (const p of filtrate) {
      const arr = by.get(p.categorie) ?? []
      arr.push(p)
      by.set(p.categorie, arr)
    }
    const ordered: { categorie: string; produse: Produs[] }[] = []
    for (const c of CATEGORII) {
      const arr = by.get(c)
      if (arr?.length) ordered.push({ categorie: c, produse: arr })
    }
    for (const [c, arr] of by) {
      if (!CATEGORII.includes(c as Categorie)) ordered.push({ categorie: c, produse: arr })
    }
    return ordered
  }, [filtrate])

  let rowIndex = 0

  return (
    <div>
      <ScreenHeader tab="catalog">
        <div className="relative">
          <PackageSearch className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={cauta}
            onChange={(e) => setCauta(e.target.value)}
            placeholder="Caută produs sau cod…"
            className="h-9 w-52 pl-8"
          />
        </div>
        <Select value={filtruCat} onValueChange={(v) => setFiltruCat(v as 'toate' | Categorie)}>
          <SelectTrigger className="h-9 w-40">
            <SelectValue placeholder="Categorie" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="toate">Toate categoriile</SelectItem>
            {CATEGORII.map((c) => (
              <SelectItem key={c} value={c}>
                {c}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </ScreenHeader>

      <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatTile label="Produse" value={String(summary.nr)} icon={Drumstick} index={0} />
        <StatTile
          label="Valoare stoc"
          value={fmtRON(summary.valoare)}
          icon={Warehouse}
          tone="amber"
          index={1}
        />
        <StatTile label="În stoc" value={fmtKg(summary.totalKg)} icon={PackageSearch} index={2} />
        <StatTile
          label="Sub prag"
          value={`${summary.subPrag} produse`}
          icon={TriangleAlert}
          tone={summary.subPrag > 0 ? 'tomato' : 'ink'}
          index={3}
        />
      </div>

      <div className="overflow-hidden rounded-xl border border-border bg-card shadow-paper">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="w-16">Cod</TableHead>
              <TableHead>Produs</TableHead>
              <TableHead className="hidden sm:table-cell">Categorie</TableHead>
              <TableHead className="text-right">Preț / kg</TableHead>
              <TableHead className="text-right">Stoc</TableHead>
              <TableHead className="hidden md:table-cell">Abator</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {grupuri.length === 0 && (
              <TableRow className="hover:bg-transparent">
                <TableCell colSpan={6} className="py-10 text-center text-sm text-muted-foreground">
                  Niciun produs nu se potrivește căutării.
                </TableCell>
              </TableRow>
            )}
            {grupuri.map(({ categorie, produse: rows }) => (
              <Fragment key={categorie}>
                {filtruCat === 'toate' && (
                  <TableRow className="hover:bg-transparent">
                    <TableCell
                      colSpan={6}
                      className="bg-muted/40 py-1.5 text-[0.7rem] font-semibold uppercase tracking-wide text-muted-foreground"
                    >
                      {categorie} · {rows.length}
                    </TableCell>
                  </TableRow>
                )}
                {rows.map((p) => {
                  const low = p.stocKg < PRAG_STOC
                  const i = rowIndex++
                  return (
                    <motion.tr
                      key={p.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.3, delay: Math.min(i * 0.02, 0.3) }}
                      className="border-b border-border transition-colors hover:bg-accent/50"
                    >
                      <TableCell className="font-mono text-xs text-muted-foreground">
                        {p.cod}
                      </TableCell>
                      <TableCell className="font-medium">{p.nume}</TableCell>
                      <TableCell className="hidden sm:table-cell">
                        <Badge variant={catVariant[p.categorie] ?? 'muted'}>{p.categorie}</Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <PretInput id={p.id} value={p.pretKg} />
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          {low && (
                            <Badge variant="danger" className="gap-1">
                              <TriangleAlert className="size-3" />
                              stoc redus
                            </Badge>
                          )}
                          <span
                            className={cn(
                              'font-mono text-sm nums',
                              low ? 'font-semibold text-tomato' : 'text-foreground',
                            )}
                          >
                            {fmtSuma(p.stocKg)}
                          </span>
                          <span className="text-xs text-muted-foreground">kg</span>
                        </div>
                      </TableCell>
                      <TableCell className="hidden md:table-cell text-sm text-muted-foreground">
                        {p.abator}
                      </TableCell>
                    </motion.tr>
                  )
                })}
              </Fragment>
            ))}
          </TableBody>
        </Table>
      </div>

      <p className="mt-3 text-xs text-muted-foreground">
        Prețurile se salvează automat la editare — modificarea se vede imediat pe comenzi și
        facturi.
      </p>
    </div>
  )
}
